import React, { useState, useEffect, useRef } from 'react';

const ChatPanel = ({ client, channelName, userType, userName }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  // Écouter les messages entrants 
  useEffect(() => {
    const handleChatMessage = (event) => {
      const message = event.detail;
      setMessages(prev => [...prev, {
        ...message,
        id: Date.now() + Math.random(),
        timestamp: new Date().toLocaleTimeString()
      }]);
      
      // Incrémenter les non-lus si panel fermé
      if (!isVisible) {
        setUnreadCount(prev => prev + 1);
      }
    };

    window.addEventListener('agora-chat-message', handleChatMessage);
    
    return () => {
      window.removeEventListener('agora-chat-message', handleChatMessage);
    };
  }, [isVisible]);

  useEffect(() => {
    // Auto-scroll vers le bas
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const togglePanel = () => {
    const newVisible = !isVisible;
    setIsVisible(newVisible);
    if (newVisible) {
      setUnreadCount(0); // Reset à l'ouverture
    }
  };

  const sendMessage = async () => {
    console.log('🚀 Début envoi message');
    console.log('Client disponible:', !!client);
    console.log('Message à envoyer:', newMessage);
    
    if (!newMessage.trim() || !client) {
        console.log('❌ Arrêt: message vide ou client indisponible');
        return;
    }
          
    const message = {
      type: 'chat',
      text: newMessage,
      sender: userName,
      userType: userType,
      timestamp: new Date().toLocaleTimeString()
    };
      
    try {
      console.log('📤 Tentative envoi via Agora stream...');
      
      // Envoyer via Agora stream message
      const encoder = new TextEncoder();
      await client.sendStreamMessage({
        payload: encoder.encode(JSON.stringify(message))  
      });
      
      console.log('✅ Message envoyé avec succès');
      
      // Ajouter à la liste locale
      setMessages(prev => [...prev, {
        ...message,
        id: Date.now(),
        isOwn: true
      }]);

      setNewMessage('');
    } catch (error) {
      console.error('❌ Erreur envoi message:', error);
      console.error('Détails erreur:', error.code, error.message);
      alert('Impossible d\'envoyer le message: ' + error.message);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Badge de notification */}
      {unreadCount > 0 && !isVisible && (
        <div style={{
          position: 'fixed',
          bottom: '65px',
          right: '65px',
          backgroundColor: '#dc3545',
          color: 'white',
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 'bold',
          zIndex: 1001,
          boxShadow: '0 2px 8px rgba(220, 53, 69, 0.4)'
        }}>
          {unreadCount}
        </div>
      )}

      {/* Bouton toggle chat */}
      <button
        onClick={togglePanel}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: isVisible ? '#28a745' : '#4CAF50',
          color: 'white',
          border: 'none',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000,
          transition: 'all 0.3s ease'
        }}
        title={isVisible ? 'Fermer Chat' : 'Ouvrir Chat'}
      >
        {isVisible ? '✕' : '💬'}
      </button>

      {/* Panel de chat */}
      {isVisible && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          width: '350px',
          height: '400px',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 999
        }}>
          {/* Header */}
          <div style={{
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px 12px 0 0',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '16px' }}>💬 Chat du Cours</h4>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                {messages.length} message(s) • Temps réel
              </p>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '5px',
                color: '#666'
              }}
            >
              ✕
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            padding: '10px',
            overflowY: 'auto',
            backgroundColor: '#f9f9f9'
          }}>
            {messages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                color: '#666',
                padding: '20px',
                fontSize: '14px'
              }}>
                Aucun message pour le moment.<br/>
                Commencez la conversation ! 👋
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    marginBottom: '12px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: msg.isOwn ? '#e3f2fd' : 'white',
                    border: msg.isOwn ? '1px solid #2196f3' : '1px solid #eee',
                    alignSelf: msg.isOwn ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '4px',
                    fontWeight: 'bold'
                  }}>
                    {msg.userType === 'prof' ? '👨‍🏫' : '👨‍🎓'} {msg.sender}
                    <span style={{ float: 'right', fontWeight: 'normal' }}>
                      {msg.timestamp}
                    </span>
                  </div>
                  <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                    {msg.text}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input message */}
          <div style={{
            padding: '15px',
            borderTop: '1px solid #eee',
            backgroundColor: 'white',
            borderRadius: '0 0 12px 12px'
          }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Tapez votre message..."
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  resize: 'none',
                  height: '40px',
                  fontSize: '14px'
                }}
                rows={1}
              />
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: newMessage.trim() ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px'
                }}
              >
                📤
              </button>
            </div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '5px'
            }}>
              Appuyez sur Entrée pour envoyer
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatPanel;
